// Escopo dos itens do catálogo na tela de permissões. Na API um item é
// { key, resource_ids }: resource_ids nulo = todos os recursos; lista = só esses.
// Na tela o escopo de cada item marcado é { all, ids }.

// Como cada tipo de recurso aparece no escopo dos itens scopable.
const RESOURCE_WORDS = {
  project: { all: "Todos os projetos", many: "projetos", one: "projeto" },
  confidential_group: { all: null, many: "grupos", one: "grupo" },
};

/** Palavras do tipo de recurso de um item do catálogo (projeto, se não for conhecido). */
export function resourceWords(catalogItem) {
  return RESOURCE_WORDS[catalogItem?.resource_type] || RESOURCE_WORDS.project;
}

/**
 * Texto curto do escopo de um item da API (com `resources` para os nomes).
 * `catalogItem` diz o tipo de recurso (projetos, grupos confidenciais).
 */
export function scopeLabel(item, catalogItem) {
  const words = resourceWords(catalogItem);
  if (item.resource_ids === null || item.resource_ids === undefined) {
    return { text: words.all };
  }
  const names = (item.resources || []).map((r) => r.name);
  const title = names.join(", ");
  if (names.length <= 2) return { text: title, title };
  return { text: `${names.length} ${words.many}`, title };
}

/** Itens da API → estado da tela: { [key]: { all, ids } }. */
export function selectionFromItems(items) {
  const selection = {};
  for (const item of items) {
    const all = item.resource_ids === null || item.resource_ids === undefined;
    selection[item.key] = { all, ids: all ? [] : [...item.resource_ids] };
  }
  return selection;
}

/** Estado da tela → corpo do PUT, na ordem do catálogo. */
export function itemsFromSelection(selection, catalogItems) {
  return catalogItems
    .filter((item) => selection[item.key])
    .map((item) => {
      const scope = selection[item.key];
      return { key: item.key, resource_ids: !item.scopable || scope.all ? null : [...scope.ids] };
    });
}

/** Junta dois escopos: "todos" vence; senão, a união dos ids. */
export function mergeScope(a, b) {
  if (a.all || b.all) return { all: true, ids: [] };
  return { all: false, ids: [...new Set([...a.ids, ...b.ids])] };
}

/**
 * Mesma regra do back (normalizeItems): todo item marcado leva junto os itens de
 * que depende, com pelo menos o mesmo escopo.
 */
export function withRequirements(selection, catalogItems) {
  const byKey = new Map(catalogItems.map((item) => [item.key, item]));
  const result = { ...selection };
  const add = (key, scope) => {
    result[key] = result[key] ? mergeScope(result[key], scope) : { ...scope, ids: [...scope.ids] };
    for (const dep of byKey.get(key)?.requires || []) add(dep, scope);
  };
  for (const [key, scope] of Object.entries(selection)) add(key, scope);
  return result;
}

/** Itens marcados que dependem de `key` (e por isso o mantêm marcado). */
export function dependentsOf(key, selection, catalogItems) {
  return catalogItems.filter((item) => selection[item.key] && item.requires.includes(key));
}

/**
 * Estado da tela a partir de um template do sistema: só os itens dele, todos
 * valendo para "todos" (substitui o que estava marcado). As dependências entram
 * pela mesma regra de sempre, caso o template venha sem alguma. Os itens que não
 * entram em template (`templatable: false`, ex.: "Ver dados confidenciais") ficam
 * como estavam em `current`: só saem desmarcando.
 */
export function selectionFromTemplate(template, catalogItems, current = {}) {
  const selection = {};
  for (const item of catalogItems) {
    if (item.templatable === false && current[item.key]) selection[item.key] = current[item.key];
  }
  for (const key of template.items) selection[key] = { all: true, ids: [] };
  return withRequirements(selection, catalogItems);
}
